import React from 'react';
import { Spinner } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { FormField } from '@components/common/FormField/FormField';
import { useInlineFilterEditorStyles } from './InlineFilterEditor.styles';
import type { FieldDefinition } from '@store/models/booking';

export interface InlineFilterEditorProps {
  filterFields: FieldDefinition[];
  formData: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  onUpdate: () => void | Promise<void>;
  onCancel: () => void;
  validationErrors: Record<string, string>;
  isUpdating?: boolean;
}

export const InlineFilterEditor: React.FC<InlineFilterEditorProps> = ({
  filterFields,
  formData,
  onFieldChange,
  onUpdate,
  onCancel,
  validationErrors,
  isUpdating = false,
}) => {
  const { t } = useTranslation('bookingForm');
  const styles = useInlineFilterEditorStyles();

  return (
    <div className={styles.root}>
      <div className={styles.fieldsGrid}>
        {filterFields.map((field) => (
          <FormField
            key={field.key}
            field={field}
            value={formData[field.key]}
            onChange={onFieldChange}
            error={validationErrors[field.key]}
            formData={formData}
          />
        ))}
      </div>
      <div className={styles.buttonRow}>
        <BasicButton
          variant="primary"
          size="small"
          onClick={() => {
            void onUpdate();
          }}
          disabled={isUpdating}
          icon={isUpdating ? <Spinner size="tiny" /> : undefined}
          iconPosition="before"
          aria-busy={isUpdating}
        >
          {t('bookingForm.buttons.update')}
        </BasicButton>
        <BasicButton variant="outline" size="small" onClick={onCancel} disabled={isUpdating}>
          {t('bookingForm.buttons.cancel')}
        </BasicButton>
      </div>
    </div>
  );
};

InlineFilterEditor.displayName = 'InlineFilterEditor';
